import React, { useMemo, useState, useCallback } from 'react'
import { Field, formValueSelector, change } from 'redux-form'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import QS from 'qs'

import { adaloBackendAxios } from 'utils/io/http/axios'
import { readTruncationWarningFromHeaders } from 'utils/sheetbridge'

import { GroupedAccordion } from 'components/Shared/Accordion'
import EmptyState from 'components/Shared/EmptyState'
import Loading from 'components/Shared/Loading'
import Button from 'components/Shared/Button'
import SheetBridgeTruncationWarning from 'components/Shared/SheetBridgeTruncationWarning.tsx'
import Icon from 'components/Shared/Icon'
import ToggleCheckbox from 'components/Shared/Forms/ToggleCheckbox'
import SelectAllCheckbox from './SelectAllCheckbox'

import { FORM_NAME } from './index'

const TEST_IDLE = 'idle'
const TEST_LOADING = 'loading'
const TEST_SUCCESS = 'success'
const TEST_FAILURE = 'failure'

const SheetItem = ({ sheet, bridgeId, existingCollectionNames }) => {
  const [testStatus, setTestStatus] = useState(TEST_IDLE)
  const [testError, setTestError] = useState(null)
  const [testResponse, setTestResponse] = useState(null)

  const selector = formValueSelector(FORM_NAME)
  const checkboxName = `bridges.${bridgeId}-${sheet.id}`
  const selected = useSelector(state => selector(state, checkboxName))

  const collectionName = sheet.name
  const isAlreadyAdded = existingCollectionNames.includes(collectionName)

  // For already-added collections, show as checked but disabled
  const isChecked = isAlreadyAdded ? true : selected

  const hasAuthHeader = sheet.authHeaderName || sheet.authHeaderEncrypted

  const handleRunTest = useCallback(async () => {
    if (!sheet.apiUrl) {
      setTestStatus(TEST_FAILURE)
      setTestError('No API URL configured')

      return
    }

    setTestStatus(TEST_LOADING)
    setTestError(null)
    setTestResponse(null)

    try {
      // Build auth array in the same format as external collections
      const auth = sheet.authHeaderEncrypted
        ? [
            {
              type: 'header',
              name: sheet.authHeaderName || 'Authorization',
              value: sheet.authHeaderEncrypted,
              encrypted: true,
            },
          ]
        : []

      // Use existing test-api-endpoint - database service will decrypt
      // Use QS.stringify to properly serialize the auth array (same as external collections)
      const query = QS.stringify({
        url: sheet.apiUrl,
        auth,
        method: 'get',
      })

      const { data } = await adaloBackendAxios.get(
        `/test-api-endpoint?${query}`
      )

      if (data.success) {
        const results = data.response?.data
        const truncationWarning = readTruncationWarningFromHeaders(
          data.response?.headers
        )

        setTestStatus(TEST_SUCCESS)
        setTestResponse({
          status: data.response?.status,
          itemCount: Array.isArray(results) ? results.length : 1,
          data: results,
          truncationWarning,
        })
      } else {
        setTestStatus(TEST_FAILURE)
        setTestError(data.error || 'Test failed')
      }
    } catch (error) {
      setTestStatus(TEST_FAILURE)
      setTestError(error.message || 'Failed to connect')
    }
  }, [sheet.apiUrl, sheet.authHeaderEncrypted, sheet.authHeaderName])

  const handleBack = useCallback(() => {
    setTestStatus(TEST_IDLE)
    setTestError(null)
    setTestResponse(null)
  }, [])

  return (
    <>
      <tr
        className={classNames('sheet-item', {
          selected: isChecked,
          'already-added': isAlreadyAdded,
        })}
      >
        <td className="sheet-checkbox-cell">
          {isAlreadyAdded ? (
            <ToggleCheckbox
              input={{ value: true, onChange: () => {} }}
              disabled
            />
          ) : (
            <Field component={ToggleCheckbox} name={checkboxName} />
          )}
        </td>
        <td className="sheet-name-cell">
          <div className="sheet-name-wrapper">
            <Field
              name={`sheetNames.${bridgeId}-${sheet.id}`}
              component="input"
              type="text"
              placeholder={sheet.name}
              autoComplete="off"
              disabled={isAlreadyAdded}
              className="sheet-name-input"
            />
            <Icon type="pencil-small" className="sheet-name-edit-icon" />
          </div>
        </td>
        <td className="sheet-api-url-cell">
          <span className="sheet-api-url">{sheet.apiUrl}</span>
        </td>
        <td className="sheet-auth-cell">
          {hasAuthHeader ? (
            <span className="sheet-auth-bearer">
              Bearer Token :{' '}
              {sheet.token_prefix && sheet.token_suffix ? (
                <span className="sheet-auth-token-masked">
                  {sheet.token_prefix}••••••••••{sheet.token_suffix}
                </span>
              ) : (
                '***'
              )}
            </span>
          ) : (
            <span className="sheet-auth-empty">-</span>
          )}
        </td>
        <td className="sheet-actions-cell">
          <Button
            teal
            small
            onClick={handleRunTest}
            disabled={testStatus === TEST_LOADING || isAlreadyAdded}
            className="run-test-button"
            loading={testStatus === TEST_LOADING}
          >
            Run Test
          </Button>
        </td>
      </tr>
      {(testStatus === TEST_SUCCESS || testStatus === TEST_FAILURE) && (
        <tr className="sheet-test-results-row">
          <td colSpan={5} className="sheet-test-results-cell">
            <div className="sheet-test-results-content">
              <button
                onClick={handleBack}
                type="button"
                className="test-results-close-button"
              >
                Close
              </button>
              {testStatus === TEST_SUCCESS ? (
                <div className="test-success-content">
                  <h4>
                    {testResponse?.truncationWarning
                      ? 'Test completed with issues'
                      : 'Test Successful!'}
                  </h4>
                  <SheetBridgeTruncationWarning
                    message={testResponse?.truncationWarning}
                  />
                  <p>Here's the response we got back:</p>
                  <pre>{JSON.stringify(testResponse?.data, null, 2)}</pre>
                </div>
              ) : (
                <div className="test-error-content">
                  <h4>An error occurred</h4>
                  <p className="error-message">{testError}</p>
                  <p className="url-label">URL Called:</p>
                  <pre>{sheet.apiUrl}</pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

const BridgeDropdown = ({ bridge, existingCollectionNames }) => {
  const dispatch = useDispatch()
  const selector = formValueSelector(FORM_NAME)
  const formBridges = useSelector(state => selector(state, 'bridges')) || {}

  const allSheets = useMemo(() => {
    return bridge.sheets || []
  }, [bridge.sheets])

  const availableSheets = useMemo(() => {
    return allSheets.filter(
      sheet => !existingCollectionNames.includes(sheet.name)
    )
  }, [allSheets, existingCollectionNames])

  const selectedSheetsCount = useMemo(() => {
    return availableSheets.filter(sheet => {
      const checkboxName = `${bridge.id}-${sheet.id}`

      return formBridges[checkboxName]
    }).length
  }, [availableSheets, bridge.id, formBridges])

  const isAllSelected =
    availableSheets.length > 0 && selectedSheetsCount === availableSheets.length
  const isIndeterminate =
    selectedSheetsCount > 0 && selectedSheetsCount < availableSheets.length

  const handleSelectAll = useCallback(() => {
    const shouldSelect = !isAllSelected

    availableSheets.forEach(sheet => {
      const checkboxName = `bridges.${bridge.id}-${sheet.id}`
      dispatch(change(FORM_NAME, checkboxName, shouldSelect))
    })
  }, [availableSheets, bridge.id, dispatch, isAllSelected])

  if (allSheets.length === 0) {
    return null
  }

  const availableSheetsCount = availableSheets.length

  return (
    <div className="bridge-dropdown">
      <GroupedAccordion
        boxed
        color="orange"
        hideDone
        group={`sheetbridge-bridge-${bridge.id}`}
        itemId={bridge.id}
        title={
          <div className="bridge-dropdown-title">
            <p>{bridge.name}</p>
            <p className="bridge-sheet-count">
              {allSheets.length} sheet{allSheets.length !== 1 ? 's' : ''}
              {availableSheetsCount < allSheets.length && (
                <span className="already-added-count">
                  {' '}
                  ({allSheets.length - availableSheetsCount} already added)
                </span>
              )}
            </p>
          </div>
        }
        renderChildren={() => (
          <div className="bridge-sheets-table-wrapper">
            <table className="bridge-sheets-table">
              <thead>
                <tr>
                  <th className="sheet-checkbox-header">
                    {availableSheetsCount > 0 && (
                      <div className="select-all-wrapper">
                        <SelectAllCheckbox
                          input={{
                            value: isAllSelected,
                            onChange: handleSelectAll,
                          }}
                          indeterminate={isIndeterminate}
                        />
                      </div>
                    )}
                  </th>
                  <th className="sheet-name-header">NAME</th>
                  <th className="sheet-api-url-header">API URL</th>
                  <th className="sheet-auth-header">AUTHORIZATION</th>
                  <th className="sheet-actions-header">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {allSheets.map(sheet => (
                  <SheetItem
                    key={sheet.id}
                    sheet={{ ...sheet, bridgeName: bridge.name }}
                    bridgeId={bridge.id}
                    existingCollectionNames={existingCollectionNames}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      />
    </div>
  )
}

const SheetBridgeBridgesForm = props => {
  const { bridges, status, datasource } = props

  const existingCollectionNames = useMemo(() => {
    if (datasource?.tables) {
      return Object.entries(datasource.tables).map(([key, value]) => value.name)
    }

    return []
  }, [JSON.stringify(datasource?.tables || {})])

  const filteredBridges = useMemo(() => {
    if (!bridges || !Array.isArray(bridges)) return []

    // Show all bridges - don't filter out ones with existing collections
    // The individual sheets will be marked as disabled if already added
    return bridges
  }, [bridges])

  if (status === 'loading') {
    return (
      <EmptyState greedy>
        <Loading />
      </EmptyState>
    )
  }

  if (status === 'error') {
    return <EmptyState greedy>Error loading bridges</EmptyState>
  }

  if (filteredBridges.length === 0) {
    return (
      <EmptyState greedy>
        <p>No bridges found.</p>
        <p>Create bridges in SheetBridge to connect them here.</p>
      </EmptyState>
    )
  }

  return (
    <div className="sheetbridge-bridges-form">
      <div className="bridges-header">
        <h3>Select Sheets to Add</h3>
        <p>
          Expand each bridge to view and select sheets to add as collections
        </p>
      </div>
      <div className="bridges-list">
        {filteredBridges.map(bridge => (
          <BridgeDropdown
            key={bridge.id}
            bridge={bridge}
            existingCollectionNames={existingCollectionNames}
          />
        ))}
      </div>
    </div>
  )
}

export default SheetBridgeBridgesForm
